import React, { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import Tabs from '~/components/common/Tabs';
import { AdminOptionTabs } from '~/models/adminOptions';
import {
  ADMIN_OPTIONS_OPTIONS,
  AdminOptionTutorial,
} from '~/constants/adminOptions';
import themes from '~/themes';
import { useAuth } from '~/providers/AuthProvider';
import { USER_ROLE } from '~/constants/userManagement';
import Tutorial from '~/components/common/Tutorial';
import { PageLayout } from '~/layout/PageLayout';
import { useWorkspaceNavigate } from '~/hooks/useWorkspaceNavigate';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useNotificationContext } from 'bs-unified-ui';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { Box } from '@mui/material';
import { useDeliveryReminders } from '~/hooks/useDeliveryReminders';
import { usePaymentInfo } from '~/providers/PaymentProvider';
import { IPlanTier } from '~/models/plan';
import { has } from 'lodash';
import { UserList } from './Users/UserList';
import { DriverList } from './Drivers/List';
import General from './General';
import Teams from './Teams';
import Organization from './Organization';
import { Billing } from './Billing';
import { Stores } from './Stores';
import { Plans } from './Plans';

export interface IProps {}

const AdminOptions: React.FC<IProps> = () => {
  const params = useParams();
  const navigate = useWorkspaceNavigate();
  const { account, getTooltipOpen, updateUserTooltip } = useAuth();
  const isNotiV2 = useFeatureIsOn('cs_9035_notification');

  const { org: orgInfo } = usePaymentInfo();

  const isBundlePlan =
    orgInfo?.tier === IPlanTier.Advance && !has(orgInfo, 'subscription_id');

  const notificationContext = useNotificationContext();
  const indicators =
    'indicators' in notificationContext
      ? notificationContext.indicators
      : undefined;

  const { newDriver } = useDeliveryReminders();

  const newDriverIndicator = isBundlePlan ? indicators?.new_driver : newDriver;

  const source = (
    account?.role === USER_ROLE.ADMIN || account?.role === USER_ROLE.OWNER
      ? params?.source || AdminOptionTabs.General
      : AdminOptionTabs.General
  ) as AdminOptionTabs;

  const handleChangeTab = useCallback((val: string) => {
    navigate(`/setting${val === AdminOptionTabs.General ? '' : `/${val}`}`);
  }, []);

  useEffect(() => {
    if (
      isBundlePlan &&
      (source === AdminOptionTabs.Plans || source === AdminOptionTabs.Billing)
    ) {
      navigate('/setting');
    }
  }, [isBundlePlan, source, navigate]);

  const tabsData = useMemo(() => {
    const options =
      account?.role === USER_ROLE.ADMIN || account?.role === USER_ROLE.OWNER
        ? ADMIN_OPTIONS_OPTIONS
        : ADMIN_OPTIONS_OPTIONS.slice(0, 1);

    if (isBundlePlan) {
      return options.filter(
        (item) =>
          item.value !== AdminOptionTabs.Plans &&
          item.value !== AdminOptionTabs.Billing,
      );
    }
    return options;
  }, [source, account?.role, isBundlePlan]);

  const tabs = useMemo(
    () =>
      tabsData.map((item) => {
        const shouldShowDot =
          isNotiV2 &&
          item.value === AdminOptionTabs.Drivers &&
          newDriverIndicator;

        const labelContent =
          source === item.value && AdminOptionTutorial[item.value] ? (
            <Tutorial
              isOpen={getTooltipOpen(AdminOptionTutorial[item.value].id)}
              onClose={() =>
                updateUserTooltip({
                  [AdminOptionTutorial[item.value].id]: true,
                })
              }
              {...AdminOptionTutorial[item.value]}
            >
              {item.label}
            </Tutorial>
          ) : (
            item.label
          );

        return {
          ...item,
          label: shouldShowDot ? (
            <Box
              sx={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              {labelContent}
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: '#F24822',
                  marginLeft: 0.5,
                }}
              />
            </Box>
          ) : (
            labelContent
          ),
        };
      }),
    [tabsData, source, account, isNotiV2, newDriverIndicator],
  );

  const contentComponent = useMemo(() => {
    switch (source) {
      case AdminOptionTabs.Users:
        return <UserList />;
      case AdminOptionTabs.Drivers:
        return <DriverList />;
      case AdminOptionTabs.Teams:
        return <Teams />;
      case AdminOptionTabs.Organization:
        return <Organization />;
      case AdminOptionTabs.Billing:
        return isBundlePlan ? <General /> : <Billing />;
      case AdminOptionTabs.Stores:
        return <Stores />;
      case AdminOptionTabs.Plans:
        return isBundlePlan ? <General /> : <Plans />;
      case AdminOptionTabs.General:
      default:
        return <General />;
    }
  }, [source, isBundlePlan]);

  return (
    <PageLayout
      headerTitle='Settings'
      sxHeaderLeftContainer={{
        [themes.breakpoints.down('md')]: {
          width: '100%',
        },
        'h2': {
          color: themes.color.violet700,
          lineHeight: '35px',
        },
        '& .MuiTabs-root': {
          '& .MuiTabs-indicator': {
            backgroundColor: `${themes.color.violet700} !important`,
          },
        },
        '& .MuiTab-root': {
          'padding': 0,
          '& .MuiTabs-indicator': {
            backgroundColor: `${themes.color.violet700} !important`,
          },
        },
        '& .MuiTabs-flexContainer': {
          gap: 2,
        },
        '& .MuiButtonBase-root': {
          'textTransform': 'unset',
          '& span': {
            color: `rgba(51, 54, 61, 0.40)`,
            fontWeight: 500,
          },
        },
        '& .Mui-selected': {
          '& span': {
            color: `${themes.color.violet700}`,
            fontWeight: 500,
          },
        },
      }}
      sxContentContainer={{
        alignItems: 'center',
        p:
          source === AdminOptionTabs.General ||
          source === AdminOptionTabs.Organization ||
          source === AdminOptionTabs.Teams
            ? themes.spacing(1.8)
            : themes.spacing(2.4),
        ...((source === AdminOptionTabs.General ||
          source === AdminOptionTabs.Organization ||
          source === AdminOptionTabs.Plans ||
          source === AdminOptionTabs.Billing ||
          source === AdminOptionTabs.Teams) && {
          backgroundColor: themes.color.gray200,
        }),
      }}
      sxHeaderContainer={{
        px: 2.4,
        pt: 2.4,
      }}
      renderTabs={
        <Tabs
          tabs={tabs}
          countWithoutBackground
          allowScrollButtonsMobile
          variant='scrollable'
          value={source}
          onChange={handleChangeTab}
        />
      }
    >
      {contentComponent}
    </PageLayout>
  );
};
export default AdminOptions;
